import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  Edit,
  Filter,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  UserMinus,
  Users,
  X,
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getWeekStart = (inputDate = new Date()) => {
  const date = new Date(inputDate);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (inputDate, numDays) => {
  const date = new Date(inputDate);
  date.setDate(date.getDate() + numDays);
  return date;
};

const toIsoDate = (inputDate) => {
  const date = new Date(inputDate);
  const tzOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

const formatTime = (value) => {
  if (!value) return '—';
  return String(value).slice(0, 5);
};

const formatTimeForInput = (value) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

const parseApiError = async (response) => {
  try {
    const data = await response.json();
    if (Array.isArray(data.detail)) {
      return data.detail.map((item) => item.msg || JSON.stringify(item)).join(', ');
    }
    return data.detail || data.message || response.statusText;
  } catch {
    return response.statusText || 'Request failed';
  }
};

const timing = (shift) =>
  shift ? `${formatTime(shift.start_time)} – ${formatTime(shift.end_time)}` : '—';

const shiftDisplayName = (shift) => {
  if (!shift) return 'Unassigned';
  return `${shift.name} (${formatTime(shift.start_time)}–${formatTime(shift.end_time)})`;
};

const Panel = ({ children, className = '' }) => (
  <div className={`rounded-lg border border-gray-100 bg-white shadow-sm ${className}`}>{children}</div>
);

const Toast = ({ toast, onClose }) => (
  <motion.div
    initial={{ opacity: 0, x: 80, scale: 0.96 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: 80, scale: 0.96 }}
    className={`rounded-lg border bg-white p-4 shadow-lg ${toast.type === 'error' ? 'border-red-200' : 'border-green-200'}`}
  >
    <div className="flex items-start gap-3">
      {toast.type === 'error' ? (
        <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
      ) : (
        <Check className="mt-0.5 h-5 w-5 text-green-600" />
      )}
      <div className="flex-1">
        <p className="font-semibold text-gray-900">{toast.title}</p>
        <p className="text-sm text-gray-600">{toast.message}</p>
      </div>
      <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  </motion.div>
);

const ModalShell = ({ title, subtitle, children, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[calc(92vh-76px)] overflow-y-auto">{children}</div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

const ShiftModal = ({ shift, onClose, onSave, busy }) => {
  const [form, setForm] = useState(() => {
    if (!shift?.id) {
      return {
        name: '',
        code: '',
        start_time: '09:00',
        end_time: '17:00',
        total_hours: 8,
        status: true,
        is_night_shift: false,
        late_mark_after: 15,
        early_leaving_before: 15,
        half_day_after: 240,
        overtime_applicable: false,
        overtime_after_hours: 8,
      };
    }
    return {
      ...shift,
      start_time: formatTimeForInput(shift.start_time),
      end_time: formatTimeForInput(shift.end_time),
      status: shift.status !== false,
      is_night_shift: Boolean(shift.is_night_shift),
      late_mark_after: shift.late_mark_after ?? 15,
      early_leaving_before: 15,
      half_day_after: 240,
      overtime_applicable: false,
      overtime_after_hours: 8,
    };
  });
  const [errors, setErrors] = useState({});

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const calcHours = () => {
    if (!form.start_time || !form.end_time) return 0;
    const [sh, sm] = form.start_time.split(':').map(Number);
    const [eh, em] = form.end_time.split(':').map(Number);
    let start = sh * 60 + sm;
    let end = eh * 60 + em;
    if (form.is_night_shift || end <= start) end += 24 * 60;
    return Math.max(Number(((end - start) / 60).toFixed(2)), 0);
  };

  useEffect(() => {
    const [sh, sm] = (form.start_time || '00:00').split(':').map(Number);
    const [eh, em] = (form.end_time || '00:00').split(':').map(Number);
    const overnight = eh * 60 + em <= sh * 60 + sm;
    setForm((prev) => ({
      ...prev,
      total_hours: calcHours(),
      is_night_shift: overnight ? true : prev.is_night_shift,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_time, form.end_time, form.is_night_shift]);

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Shift name is required.';
    if (!form.code.trim()) nextErrors.code = 'Shift code is required.';
    if (!form.start_time || !form.end_time) {
      nextErrors.time = 'Start and end times are required.';
    } else if (!form.is_night_shift) {
      const [sh, sm] = form.start_time.split(':').map(Number);
      const [eh, em] = form.end_time.split(':').map(Number);
      if (eh * 60 + em <= sh * 60 + sm) {
        nextErrors.time = 'End time must be after start time for day shifts.';
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const [sh, sm] = (form.start_time || '00:00').split(':').map(Number);
    const [eh, em] = (form.end_time || '00:00').split(':').map(Number);
    const overnight = eh * 60 + em <= sh * 60 + sm;
    onSave({
      ...form,
      code: form.code.toUpperCase(),
      is_night_shift: Boolean(form.is_night_shift || overnight),
      total_hours: calcHours(),
    });
  };

  return (
    <ModalShell
      title={shift?.id ? 'Edit Shift' : 'Add Shift'}
      subtitle="Define shift timing for hospital operations."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4 p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-gray-700">Shift Name</span>
            <input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            {errors.name && <span className="text-xs text-red-600">{errors.name}</span>}
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-gray-700">Shift Code</span>
            <input
              value={form.code}
              onChange={(e) => setField('code', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 uppercase"
            />
            {errors.code && <span className="text-xs text-red-600">{errors.code}</span>}
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-gray-700">Start Time</span>
            <input
              type="time"
              value={form.start_time}
              onChange={(e) => setField('start_time', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-gray-700">End Time</span>
            <input
              type="time"
              value={form.end_time}
              onChange={(e) => setField('end_time', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            {errors.time && <span className="text-xs text-red-600">{errors.time}</span>}
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-semibold text-gray-700">Grace Time (minutes)</span>
            <input
              type="number"
              min="0"
              value={form.late_mark_after}
              onChange={(e) => setField('late_mark_after', Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="flex items-center justify-between rounded-lg border border-gray-200 p-3 md:col-span-2">
            <span className="text-sm font-semibold text-gray-700">Night Shift</span>
            <input
              type="checkbox"
              checked={form.is_night_shift}
              onChange={(e) => setField('is_night_shift', e.target.checked)}
              className="h-5 w-5 rounded text-purple-600"
            />
          </label>
          <label className="flex items-center justify-between rounded-lg border border-gray-200 p-3 md:col-span-2">
            <span className="text-sm font-semibold text-gray-700">Active</span>
            <input
              type="checkbox"
              checked={form.status}
              onChange={(e) => setField('status', e.target.checked)}
              className="h-5 w-5 rounded text-green-600"
            />
          </label>
        </div>
        <p className="text-sm text-gray-500">
          Working hours: <span className="font-semibold text-gray-800">{form.total_hours}h</span>
        </p>
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Save Shift
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const AssignmentModal = ({ employees, shifts, initialEmployee, onClose, onSave, busy }) => {
  const [employeeId, setEmployeeId] = useState(initialEmployee?.id || employees[0]?.id || '');
  const [shiftId, setShiftId] = useState(
    initialEmployee?.shiftId || shifts.filter((s) => s.status)[0]?.id || ''
  );
  const [effectiveDate, setEffectiveDate] = useState(toIsoDate(new Date()));

  return (
    <ModalShell title="Assign Shift" subtitle="Assign a default shift for the selected week." onClose={onClose}>
      <div className="space-y-4 p-5">
        <label className="block space-y-1">
          <span className="text-sm font-semibold text-gray-700">Employee</span>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(Number(e.target.value))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name} ({emp.emp_code})
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-semibold text-gray-700">Shift</span>
          <select
            value={shiftId}
            onChange={(e) => setShiftId(Number(e.target.value))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            {shifts.filter((s) => s.status).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({timing(s)})
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-semibold text-gray-700">Week starting</span>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </label>
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onSave({ employeeId: Number(employeeId), shiftId: Number(shiftId), effectiveDate })}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            Assign
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const ShiftRoster = ({ workflowMode = 'maker' }) => {
  const { hasPermission } = useAuth();
  const readOnly = workflowMode === 'checker';
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rosterAssignments, setRosterAssignments] = useState([]);
  const [rosterBatch, setRosterBatch] = useState(null);
  const [filters, setFilters] = useState({ search: '', department: '', shiftId: '' });
  const [activeView, setActiveView] = useState('roster');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [shiftModal, setShiftModal] = useState(null);
  const [assignmentModal, setAssignmentModal] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);

  const headers = useMemo(() => getHeaders(), []);

  const notify = useCallback((title, message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, title, message, type }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const ensureCurrentWeekBatch = useCallback(async () => {
    const weekStart = getWeekStart();
    const startDate = toIsoDate(weekStart);
    const endDate = toIsoDate(addDays(weekStart, 6));
    const listRes = await fetch(buildApiUrl(`/api/v1/shifts/rosters/batches?from_date=${startDate}&to_date=${endDate}`),
      { headers }
    );
    if (!listRes.ok) throw new Error('Failed to fetch roster batches');
    const listData = await listRes.json();
    const existing =
      (listData.batches || []).find((b) => b.start_date === startDate && b.end_date === endDate) ||
      listData.batches?.[0];
    if (existing) return existing;
    if (readOnly) return null;

    const createRes = await fetch(buildApiUrl('/api/v1/shifts/rosters/batches'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        roster_type: 'weekly',
        start_date: startDate,
        end_date: endDate,
        roster_role_bucket: 'all',
        remarks: 'Weekly roster batch',
      }),
    });
    if (!createRes.ok) throw new Error('Failed to create roster batch');
    return createRes.json();
  }, [headers, readOnly]);

  const loadData = useCallback(
    async (manual = false) => {
      if (manual) setRefreshing(true);
      else setLoading(true);
      try {
        const [shiftResponse, employeeResponse, currentBatch] = await Promise.all([
          fetch(buildApiUrl('/api/v1/shifts/'), { headers }),
          fetch(buildApiUrl('/api/v1/benfish-employees/search?query=&limit=500'), { headers }),
          ensureCurrentWeekBatch(),
        ]);
        setRosterBatch(currentBatch);

        if (shiftResponse.ok) {
          const data = await shiftResponse.json();
          setShifts(data.shifts || []);
        }

        let loadedEmployees = [];
        if (employeeResponse.ok) {
          const data = await employeeResponse.json();
          loadedEmployees = (data.employees || []).map((employee) => ({
            ...employee,
            shiftId: Number(employee.shift_id || 0) || null,
            department: employee.department || '—',
          }));
          setEmployees(loadedEmployees);
        }

        if (currentBatch?.id) {
          const assignRes = await fetch(buildApiUrl(`/api/v1/shifts/rosters/assignments?from_date=${currentBatch.start_date}&to_date=${currentBatch.end_date}`),
            { headers }
          );
          if (assignRes.ok) {
            const assignData = await assignRes.json();
            const assignments = assignData.assignments || [];
            setRosterAssignments(assignments);

            const dayIndexByDate = new Map();
            days.forEach((_, dayIdx) => {
              dayIndexByDate.set(toIsoDate(addDays(new Date(currentBatch.start_date), dayIdx)), dayIdx);
            });

            const grouped = new Map();
            assignments.forEach((a) => {
              const dayIdx = dayIndexByDate.get(a.roster_date);
              if (dayIdx === undefined) return;
              const key = Number(a.employee_id);
              if (!grouped.has(key)) grouped.set(key, Array(days.length).fill(null));
              grouped.get(key)[dayIdx] = { shiftId: Number(a.shift_id), assignmentId: a.id };
            });

            const nextRosterRows = [];
            loadedEmployees.forEach((employee) => {
              const row = grouped.get(Number(employee.id));
              nextRosterRows.push({
                empId: employee.id,
                days: row ? row.map((d) => d?.shiftId ?? null) : Array(days.length).fill(null),
                dayAssignmentIds: row
                  ? row.map((d) => d?.assignmentId ?? null)
                  : Array(days.length).fill(null),
              });
            });
            setRoster(nextRosterRows);
          }
        }

        if (manual) notify('Refreshed', 'Shift and roster data loaded from server.');
      } catch {
        notify('Load failed', 'Could not load shift data. Check login and backend.', 'error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [ensureCurrentWeekBatch, headers, notify]
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shiftById = useMemo(() => {
    const map = new Map();
    shifts.forEach((s) => map.set(Number(s.id), s));
    return map;
  }, [shifts]);

  const activeShifts = useMemo(() => shifts.filter((s) => s.status), [shifts]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter((d) => d && d !== '—'))).sort(),
    [employees]
  );

  const assignmentMetaByEmployee = useMemo(() => {
    const map = new Map();
    rosterAssignments.forEach((a) => {
      const empId = Number(a.employee_id);
      const prev = map.get(empId);
      const shiftId = Number(a.shift_id) || null;
      if (!prev || a.roster_date > prev.roster_date) {
        map.set(empId, { roster_date: a.roster_date, shiftId });
      }
    });
    return map;
  }, [rosterAssignments]);

  const enrichedEmployees = useMemo(
    () =>
      employees.map((employee) => {
        const meta = assignmentMetaByEmployee.get(Number(employee.id));
        const effectiveShiftId = employee.shiftId || meta?.shiftId || null;
        const shift = effectiveShiftId ? shiftById.get(Number(effectiveShiftId)) : null;
        const isAssigned = Boolean(effectiveShiftId);
        return {
          ...employee,
          shiftId: effectiveShiftId,
          shift,
          shiftName: shiftDisplayName(shift),
          assignedDate: meta?.roster_date || '—',
          assignmentStatus: isAssigned ? 'assigned' : 'unassigned',
        };
      }),
    [employees, shiftById, assignmentMetaByEmployee]
  );

  const filteredEmployees = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return enrichedEmployees.filter((employee) => {
      const haystack = [employee.full_name, employee.emp_code, employee.department, employee.shiftName]
        .join(' ')
        .toLowerCase();
      if (search && !haystack.includes(search)) return false;
      if (filters.department && employee.department !== filters.department) return false;
      if (filters.shiftId && String(employee.shiftId) !== String(filters.shiftId)) return false;
      return true;
    });
  }, [enrichedEmployees, filters]);

  const kpis = useMemo(() => {
    const totalShifts = activeShifts.length;
    const assigned = enrichedEmployees.filter((e) => e.shiftId).length;
    const unassigned = enrichedEmployees.length - assigned;
    return { totalShifts, assigned, unassigned };
  }, [activeShifts.length, enrichedEmployees]);

  const saveShift = async (shift) => {
    if (readOnly) return;
    if (actionBusy) return;
    setActionBusy(true);
    const isEdit = Boolean(shift.id);
    try {
      const response = await fetch(isEdit ? `/api/v1/shifts/${shift.id}` : '/api/v1/shifts/', {
        method: isEdit ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({
          name: shift.name,
          code: shift.code,
          start_time: shift.start_time,
          end_time: shift.end_time,
          total_hours: shift.total_hours,
          late_mark_after: shift.late_mark_after ?? 15,
          early_leaving_before: shift.early_leaving_before ?? 15,
          half_day_after: shift.half_day_after ?? 240,
          overtime_applicable: false,
          overtime_after_hours: 8,
          is_night_shift: Boolean(shift.is_night_shift),
          status: shift.status !== false,
        }),
      });
      if (!response.ok) {
        notify('Save failed', await parseApiError(response), 'error');
        return;
      }
      setShiftModal(null);
      notify(isEdit ? 'Shift updated' : 'Shift created', `${shift.name} saved.`);
      await loadData(true);
    } finally {
      setActionBusy(false);
    }
  };

  const toggleShiftStatus = async (shift) => {
    if (readOnly) return;
    const response = await fetch(buildApiUrl(`/api/v1/shifts/${shift.id}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: !shift.status }),
    });
    if (!response.ok) {
      notify('Update failed', await parseApiError(response), 'error');
      return;
    }
    notify('Status updated', `${shift.name} is now ${shift.status ? 'inactive' : 'active'}.`);
    await loadData(true);
  };

  const deleteShift = async (shift) => {
    if (readOnly) return;
    if (!window.confirm(`Delete shift "${shift.name}"?`)) return;
    const response = await fetch(buildApiUrl(`/api/v1/shifts/${shift.id}`), { method: 'DELETE', headers });
    if (!response.ok) {
      notify('Delete failed', await parseApiError(response), 'error');
      return;
    }
    notify('Shift deleted', `${shift.name} removed.`);
    await loadData(true);
  };

  const assignShift = async ({ employeeId, shiftId, effectiveDate }) => {
    if (readOnly) return;
    if (!rosterBatch?.id) {
      notify('Roster unavailable', 'Refresh and try again.', 'error');
      return;
    }
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const weekStart = getWeekStart(new Date(effectiveDate));
      const assignments = days.map((_, index) => ({
        employee_id: Number(employeeId),
        shift_id: Number(shiftId),
        roster_date: toIsoDate(addDays(weekStart, index)),
      }));

      const response = await fetch(buildApiUrl(`/api/v1/shifts/rosters/batches/${rosterBatch.id}/assignments/bulk`),
        { method: 'POST', headers, body: JSON.stringify({ assignments }) }
      );
      if (!response.ok) {
        notify('Assignment failed', await parseApiError(response), 'error');
        return;
      }

      const masterRes = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ shift_id: Number(shiftId) }),
      });
      if (!masterRes.ok) {
        notify('Profile update failed', await parseApiError(masterRes), 'error');
        return;
      }
      const masterData = await masterRes.json();
      setEmployees((prev) =>
        prev.map((e) =>
          Number(e.id) === Number(employeeId) ? { ...e, shiftId: Number(shiftId) } : e
        )
      );

      setAssignmentModal(null);
      if (masterData.pending_profile_approval) {
        notify(
          'Shift assigned',
          'Weekly roster updated. Employee default shift is pending profile approval.',
        );
      } else {
        notify('Shift assigned', 'Employee shift and weekly roster updated.');
      }
      await loadData(true);
    } finally {
      setActionBusy(false);
    }
  };

  const updateRosterCell = async (empId, dayIndex, shiftIdValue) => {
    if (readOnly) return;
    if (!rosterBatch?.id) return;
    const rosterDate = toIsoDate(addDays(new Date(rosterBatch.start_date), dayIndex));
    const row = roster.find((r) => Number(r.empId) === Number(empId));
    const assignmentId = row?.dayAssignmentIds?.[dayIndex];

    if (!shiftIdValue) {
      if (assignmentId) {
        const delRes = await fetch(buildApiUrl(`/api/v1/shifts/rosters/assignments/${assignmentId}`), {
          method: 'DELETE',
          headers,
        });
        if (!delRes.ok) {
          notify('Update failed', await parseApiError(delRes), 'error');
          return;
        }
        notify('Roster updated', 'Shift cleared for that day.');
        await loadData(true);
      }
      return;
    }

    const response = await fetch(buildApiUrl(`/api/v1/shifts/rosters/batches/${rosterBatch.id}/assignments/bulk`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          assignments: [
            {
              employee_id: Number(empId),
              shift_id: Number(shiftIdValue),
              roster_date: rosterDate,
            },
          ],
        }),
      }
    );
    if (!response.ok) {
      notify('Roster update failed', await parseApiError(response), 'error');
      return;
    }
    await loadData(true);
  };

  const removeAssignment = async (employee) => {
    if (readOnly) return;
    if (!window.confirm(`Remove shift assignment for ${employee.full_name}?`)) return;
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const empAssignments = rosterAssignments.filter(
        (a) => Number(a.employee_id) === Number(employee.id)
      );
      for (const a of empAssignments) {
        await fetch(buildApiUrl(`/api/v1/shifts/rosters/assignments/${a.id}`), {
          method: 'DELETE',
          headers,
        });
      }
      await fetch(buildApiUrl(`/api/v1/employee-master/${employee.id}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ shift_id: null }),
      });
      notify('Assignment removed', `${employee.full_name} is unassigned.`);
      await loadData(true);
    } finally {
      setActionBusy(false);
    }
  };

  const weekLabel = rosterBatch
    ? `${rosterBatch.start_date} – ${rosterBatch.end_date}`
    : '';

  const summaryCards = [
    { label: 'Total Shifts', value: kpis.totalShifts, icon: CalendarDays, color: 'from-blue-600 to-cyan-600' },
    { label: 'Employees Assigned', value: kpis.assigned, icon: Users, color: 'from-green-600 to-emerald-500' },
    { label: 'Employees Without Shift', value: kpis.unassigned, icon: UserMinus, color: 'from-amber-500 to-orange-500' },
  ];

  const rosterRows = roster.filter((row) =>
    filteredEmployees.some((e) => Number(e.id) === Number(row.empId))
  );

  return (
    <div className="max-w-full space-y-6">
      <div className="fixed right-4 top-20 z-[70] w-[calc(100vw-2rem)] max-w-sm space-y-3">
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              toast={toast}
              onClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
            />
          ))}
        </AnimatePresence>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">HRMS / Operations</p>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Building2 className="h-7 w-7 text-blue-600" />
            Shifts &amp; Roster
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {readOnly
              ? 'View weekly roster, shift master, and assignments for audit.'
              : 'Weekly roster, shift definitions, and employee assignments.'}
            {weekLabel && (
              <span className="ml-1 font-medium text-blue-700">Week: {weekLabel}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {activeView === 'master' && !readOnly && hasPermission('shift:create') && (
            <button
              type="button"
              onClick={() => setShiftModal({})}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Shift
            </button>
          )}
          {activeView === 'assignments' && !readOnly && hasPermission('shift:assign') && (
            <button
              type="button"
              onClick={() => setAssignmentModal({})}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700"
            >
              <Users className="h-4 w-4" />
              Assign Shift
            </button>
          )}
          {readOnly && (
            <span className="text-sm text-gray-500 self-center">Read-only roster audit view</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg bg-gradient-to-br ${card.color} p-5 text-white shadow-md`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">{card.label}</p>
                <p className="mt-1 text-3xl font-bold">{card.value}</p>
              </div>
              <card.icon className="h-8 w-8 text-white/90" />
            </div>
          </div>
        ))}
      </div>

      <Panel className="p-4">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Search employee name or code"
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3"
            />
          </div>
          <select
            value={filters.department}
            onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            value={filters.shiftId}
            onChange={(e) => setFilters((prev) => ({ ...prev, shiftId: e.target.value }))}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="">All Shifts</option>
            {activeShifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setFilters({ search: '', department: '', shiftId: '' })}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-gray-700"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Filter className="h-4 w-4" />
            {filteredEmployees.length} employee(s)
          </p>
          <div className="flex rounded-lg border border-gray-200 p-1">
            {[
              { id: 'roster', label: 'Roster' },
              { id: 'master', label: 'Shift Master' },
              { id: 'assignments', label: 'Assignments' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveView(tab.id)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
                  activeView === tab.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </Panel>

      {loading ? (
        <Panel className="flex h-48 items-center justify-center text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading…
        </Panel>
      ) : (
        <>
          {activeView === 'roster' && (
            <Panel className="overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="font-bold text-gray-900">Weekly Roster</h3>
                <p className="text-sm text-gray-500">
                  {readOnly
                    ? 'Published roster for the current week (read-only).'
                    : 'Select a shift for each day. Changes are saved to the server immediately.'}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Employee</th>
                      {days.map((day) => (
                        <th key={day} className="px-3 py-3 text-left font-semibold">
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rosterRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                          No employees match filters
                        </td>
                      </tr>
                    ) : (
                      rosterRows.map((row) => {
                        const employee = enrichedEmployees.find(
                          (e) => Number(e.id) === Number(row.empId)
                        );
                        return (
                          <tr key={row.empId} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">
                                {employee?.full_name || '—'}
                              </p>
                              <p className="text-xs text-gray-500">{employee?.emp_code}</p>
                            </td>
                            {row.days.map((shiftId, dayIndex) => {
                              const shift = shiftId ? shiftById.get(Number(shiftId)) : null;
                              return (
                                <td key={`${row.empId}-${dayIndex}`} className="px-2 py-3">
                                  {readOnly ? (
                                    shift ? (
                                      <span className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800">
                                        {shift.name}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )
                                  ) : (
                                    <select
                                      value={shiftId ?? ''}
                                      onChange={(e) =>
                                        updateRosterCell(row.empId, dayIndex, e.target.value)
                                      }
                                      className="w-full min-w-[7rem] rounded border border-gray-300 px-2 py-1 text-xs"
                                    >
                                      <option value="">—</option>
                                      {activeShifts.map((s) => (
                                        <option key={s.id} value={s.id}>
                                          {s.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {activeView === 'master' && (
            <Panel className="overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="font-bold text-gray-900">Shift Master</h3>
                <p className="text-sm text-gray-500">
                  {readOnly
                    ? 'Shift definitions (view only).'
                    : 'Hospital shift definitions used in roster and assignments.'}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[800px] w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      {['Name', 'Code', 'Timing', 'Grace (min)', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shifts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                          No shifts defined
                        </td>
                      </tr>
                    ) : (
                      shifts.map((shift) => (
                        <tr key={shift.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{shift.name}</td>
                          <td className="px-4 py-3">{shift.code}</td>
                          <td className="px-4 py-3">{timing(shift)}</td>
                          <td className="px-4 py-3">{shift.late_mark_after ?? 0}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                shift.status
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {shift.status ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {readOnly ? (
                              <span className="text-xs text-gray-400">View only</span>
                            ) : (
                            <div className="flex gap-1">
                              {hasPermission('shift:edit') && (
                                <button
                                type="button"
                                onClick={() => setShiftModal(shift)}
                                className="rounded p-1.5 text-blue-600 hover:bg-blue-50"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              )}
                              <button
                                type="button"
                                onClick={() => toggleShiftStatus(shift)}
                                className="rounded p-1.5 text-orange-600 hover:bg-orange-50"
                                title="Activate / Deactivate"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                              {hasPermission('shift:delete') && (
                                <button
                                type="button"
                                onClick={() => deleteShift(shift)}
                                className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                                title="Delete"
                                disabled={shift.is_default}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                              )}
                            </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {activeView === 'assignments' && (
            <Panel className="overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="font-bold text-gray-900">Shift Assignments</h3>
                <p className="text-sm text-gray-500">
                  {readOnly
                    ? 'Employee shift assignments for audit (view only).'
                    : 'Assign or update each employee&apos;s default shift for the current week.'}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      {[
                        'Employee',
                        'Department',
                        'Current Shift',
                        'Assigned Date',
                        'Status',
                        'Actions',
                      ].map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployees.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                          No employees match filters
                        </td>
                      </tr>
                    ) : (
                      filteredEmployees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{employee.full_name}</td>
                          <td className="px-4 py-3">{employee.department}</td>
                          <td className="px-4 py-3">{employee.shiftName}</td>
                          <td className="px-4 py-3">{employee.assignedDate}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${
                                employee.shiftId
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {employee.assignmentStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {readOnly ? (
                              <span className="text-xs text-gray-400">Audit only</span>
                            ) : (
                            <div className="flex gap-2">
                              {hasPermission('shift:assign') && (
                                <button
                                  type="button"
                                  onClick={() => setAssignmentModal(employee)}
                                  className="text-sm font-medium text-blue-600 hover:underline"
                                >
                                  {employee.shiftId ? 'Change' : 'Assign'}
                                </button>
                              )}
                              {employee.shiftId && hasPermission('shift:assign') && (
                                <button
                                  type="button"
                                  onClick={() => removeAssignment(employee)}
                                  className="text-sm font-medium text-red-600 hover:underline"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}

      {shiftModal !== null && !readOnly && (
        <ShiftModal
          shift={shiftModal?.id ? shiftModal : null}
          onClose={() => setShiftModal(null)}
          onSave={saveShift}
          busy={actionBusy}
        />
      )}
      {assignmentModal !== null && !readOnly && (
        <AssignmentModal
          employees={employees}
          shifts={shifts}
          initialEmployee={assignmentModal?.id ? assignmentModal : null}
          onClose={() => setAssignmentModal(null)}
          onSave={assignShift}
          busy={actionBusy}
        />
      )}
    </div>
  );
};

export default ShiftRoster;
